import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Lenis from "lenis";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import {
  Leaf, Zap, FileText, Landmark, Boxes, SlidersHorizontal,
  ShieldCheck, ArrowRight, TrendingDown, BatteryCharging, Sun, Receipt,
} from "lucide-react";
import { inr } from "@/api";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ delay, duration: 1, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 36 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ delay, duration: 0.9, ease: EASE }}
    className={className}
  >
    {children}
  </motion.div>
);

const SLAB_PRODUCT = {
  name: "Solar Panel 550W Mono PERC",
  hsn: "85414300",
  gst: 12,
  prices: { retail: 14500, shopkeeper: 13200, dealer: 12600, distributor: 12100 },
};
const SLAB_TABS = [
  { key: "retail", label: "Retail · B2C", note: "Walk-in counter sales" },
  { key: "shopkeeper", label: "Shopkeeper", note: "Neighbourhood electrical shops" },
  { key: "dealer", label: "Dealer", note: "Regional dealerships" },
  { key: "distributor", label: "Distributor", note: "National wholesale network" },
];

const FEATURES = [
  { icon: ShieldCheck, title: "GSTIN Vendor Ledgers", desc: "Every supplier is GST-verified at entry and carries a running payable ledger with payment recording straight to your bank accounts." },
  { icon: SlidersHorizontal, title: "Multi-tier Price Slabs", desc: "Fixed Retail, Shopkeeper, Dealer and Distributor prices per product — edited inline in one matrix, applied instantly on invoices." },
  { icon: Boxes, title: "Low-Stock Alert Radar", desc: "Opening stock, reorder levels and live depletion on every sale. The dashboard flags what needs restocking before you run dry." },
  { icon: Receipt, title: "Tax Invoice Generator", desc: "GST-compliant invoices with the GREENRGY logo, HSN codes, CGST/SGST split and a print-ready, save-as-PDF layout." },
  { icon: Landmark, title: "Dual Banking & Cashflow", desc: "Multiple bank accounts with live balances fed by income, expense and party payments — daily books that always tally." },
  { icon: FileText, title: "Custom Fields Engine", desc: "Admins inject extra text, number or date fields into any form — vendors, clients, products, invoices, income or expense." },
];

const CHAPTERS = [
  { n: "01", title: "Clean tech deserves clean books", desc: "Solar panels, inverters, batteries and chargers move through a four-tier distribution chain. GREENRGY keeps every rupee of that chain reconciled — slab pricing, GST and stock in one motion." },
  { n: "02", title: "Wholesale without the chaos", desc: "Shopkeepers, dealers and distributors each see their own price the moment an invoice is raised. No rate cards, no phone calls, no disputes." },
  { n: "03", title: "Zero-error GST discipline", desc: "GSTIN-mandatory parties, automatic CGST/SGST computation, and printable tax invoices that stand up to any audit." },
];

const TICKER = ["SOLAR PANELS 550W", "HYBRID INVERTERS", "LITHIUM STORAGE", "EV CHARGING", "GST COMPLIANT", "B2B PRICE SLABS", "LED STREET LIGHTS", "STOCK RADAR", "PARTY LEDGERS", "TAX INVOICES"];

export default function Landing() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const [tab, setTab] = useState("dealer");

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  const price = SLAB_PRODUCT.prices[tab];
  const gstAmt = Math.round(price * SLAB_PRODUCT.gst) / 100;
  const margin = Math.round((1 - price / SLAB_PRODUCT.prices.retail) * 100);

  return (
    <div className="bg-[#F3F6F5] text-slate-900 min-h-screen noise-overlay relative" data-testid="landing-page">
      {/* Header */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white border-b border-slate-200" data-testid="landing-nav-header">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo-link">
            <span className="bg-white rounded-lg px-2.5 py-1 inline-flex shadow-[0_0_18px_rgba(52,211,153,0.35)]">
              <img src="/logo.png" alt="GREENRGY" className="h-6" />
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm text-slate-600">
            <a href="#slabs" className="hover:text-emerald-600 transition-colors duration-200" data-testid="nav-slabs">Price Slabs</a>
            <a href="#features" className="hover:text-emerald-600 transition-colors duration-200" data-testid="nav-features">Features</a>
            <a href="#manifesto" className="hover:text-emerald-600 transition-colors duration-200" data-testid="nav-manifesto">Manifesto</a>
          </nav>
          <Link to="/login" data-testid="nav-portal-login-btn"
            className="inline-flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm px-5 py-2 transition-colors duration-200">
            Portal Login <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section ref={heroRef} className="relative pt-36 pb-24 px-5 sm:px-8 overflow-hidden" data-testid="landing-hero-section">
        <div className="hero-grid-bg absolute inset-0" />
        <div className="absolute -top-32 left-1/4 w-[520px] h-[380px] bg-emerald-500/12 blur-[130px] rounded-full animate-pulse-glow" />
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 items-center relative">
          <motion.div style={{ y: textY }} className="md:col-span-7">
            <MaskedLine delay={0.1}>
              <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-4 py-1.5 text-xs font-mono2 uppercase tracking-[0.2em] text-emerald-600">
                <Zap className="w-3.5 h-3.5" /> Green energy distribution OS
              </span>
            </MaskedLine>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              <MaskedLine delay={0.25}>Precision accounting</MaskedLine>
              <MaskedLine delay={0.37}>for the <span className="text-gradient-green">green energy</span></MaskedLine>
              <MaskedLine delay={0.49}>supply chain.</MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.9, ease: EASE }}
              className="mt-6 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed"
            >
              B2B price slabs for Shopkeepers, Dealers and Distributors. GSTIN-verified ledgers,
              tax invoices with your logo, live stock and daily cashflow — one portal, zero spreadsheets.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.9, ease: EASE }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <a href="#slabs" data-testid="hero-explore-slabs-btn"
                className="inline-flex items-center gap-2 rounded-full border border-emerald-500/50 text-emerald-600 hover:bg-emerald-500 hover:text-slate-950 font-semibold px-6 py-3 transition-colors duration-200">
                Explore Price Slabs
              </a>
              <Link to="/login" data-testid="hero-access-portal-btn"
                className="inline-flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold px-6 py-3 transition-colors duration-200">
                Access Portal <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.15, duration: 1 }}
              className="mt-10 flex gap-8 font-mono2"
              data-testid="hero-stats-badge"
            >
              {[["4", "price tiers"], ["18%", "max GST handled"], ["100%", "GSTIN verified"]].map(([v, l]) => (
                <div key={l}>
                  <div className="text-2xl font-bold text-emerald-600">{v}</div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mt-1">{l}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div style={{ y: imgY }} className="md:col-span-5 relative"
            initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1.1, ease: EASE }}>
            <div className="absolute -inset-4 bg-emerald-500/15 blur-3xl rounded-full" />
            <div className="relative rounded-3xl overflow-hidden border border-emerald-500/30 shadow-[0_0_60px_rgba(16,185,129,0.18)]">
              <img
                src="https://images.unsplash.com/photo-1615630859219-0459703c34e6?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Solar panel array" className="w-full h-[420px] object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 rounded-2xl backdrop-blur-xl bg-white/90 border border-slate-300/60 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
                    <Sun className="w-4 h-4 text-emerald-600" />
                  </span>
                  <div>
                    <p className="text-xs text-slate-600">Solar Panel 550W</p>
                    <p className="font-mono2 font-bold text-emerald-600">{inr(12100)} <span className="text-[10px] text-slate-500 font-normal">distributor slab</span></p>
                  </div>
                </div>
                <BatteryCharging className="w-5 h-5 text-lime-600" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Marquee */}
      <div className="border-y border-slate-200 bg-white/70 py-4 overflow-hidden" data-testid="landing-marquee-ticker">
        <div className="flex whitespace-nowrap animate-marquee w-max">
          {[...TICKER, ...TICKER].map((t, i) => (
            <span key={i} className="mx-6 inline-flex items-center gap-3 text-xs font-mono2 uppercase tracking-[0.25em] text-slate-500">
              <Leaf className="w-3.5 h-3.5 text-emerald-500/70" /> {t}
            </span>
          ))}
        </div>
      </div>

      {/* Price slab explainer */}
      <section id="slabs" className="max-w-7xl mx-auto px-5 sm:px-8 py-28" data-testid="landing-price-slab-explainer">
        <Reveal>
          <p className="text-xs font-mono2 uppercase tracking-[0.2em] text-emerald-600 font-semibold mb-3">The slab engine</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-2xl">
            One product. Four honest prices. <span className="text-gradient-green">Zero negotiation.</span>
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <Reveal className="lg:col-span-5" delay={0.1}>
            <div className="space-y-3">
              {SLAB_TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`slab-tab-${t.key}`}
                  className={`w-full text-left rounded-2xl border p-5 transition-colors duration-300 ${
                    tab === t.key
                      ? "border-emerald-500/60 bg-emerald-500/10 shadow-[0_0_30px_rgba(16,185,129,0.15)]"
                      : "border-slate-200 bg-white/80 hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`font-semibold ${tab === t.key ? "text-emerald-700" : "text-slate-800"}`}>{t.label}</span>
                    <span className="font-mono2 font-bold text-lg text-slate-900">{inr(SLAB_PRODUCT.prices[t.key])}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{t.note}</p>
                </button>
              ))}
            </div>
          </Reveal>
          <Reveal className="lg:col-span-7" delay={0.2}>
            <div className="rounded-3xl border border-emerald-500/30 bg-white p-8 shadow-[0_0_35px_rgba(16,185,129,0.12)]" data-testid="slab-live-invoice-demo">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-xs font-mono2 uppercase tracking-[0.2em] text-slate-500">Live invoice preview</p>
                  <h3 className="text-lg font-semibold mt-1">{SLAB_PRODUCT.name}</h3>
                  <p className="text-xs text-slate-500 font-mono2 mt-0.5">HSN {SLAB_PRODUCT.hsn} · GST {SLAB_PRODUCT.gst}%</p>
                </div>
                <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-xs font-mono2 uppercase tracking-wider text-emerald-600">
                  {SLAB_TABS.find((t) => t.key === tab)?.label}
                </span>
              </div>
              <div className="space-y-3 font-mono2 text-sm">
                <div className="flex justify-between text-slate-600"><span>Slab rate</span><motion.span key={tab + "p"} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="text-slate-900 font-bold">{inr(price)}</motion.span></div>
                <div className="flex justify-between text-slate-600"><span>CGST {SLAB_PRODUCT.gst / 2}%</span><span className="text-slate-900">{inr(gstAmt / 2)}</span></div>
                <div className="flex justify-between text-slate-600"><span>SGST {SLAB_PRODUCT.gst / 2}%</span><span className="text-slate-900">{inr(gstAmt / 2)}</span></div>
                <div className="h-px bg-slate-100" />
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Invoice total</span>
                  <motion.span key={tab + "t"} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                    className="text-2xl font-bold text-emerald-600">{inr(price + gstAmt)}</motion.span>
                </div>
              </div>
              {margin > 0 && (
                <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-lime-500/10 border border-lime-500/30 px-4 py-1.5 text-xs text-lime-600">
                  <TrendingDown className="w-3.5 h-3.5" /> {margin}% below retail for this tier
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Features bento */}
      <section id="features" className="max-w-7xl mx-auto px-5 sm:px-8 pb-28" data-testid="landing-features-grid">
        <Reveal>
          <p className="text-xs font-mono2 uppercase tracking-[0.2em] text-emerald-600 font-semibold mb-3">Capabilities</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Everything the back office was missing.</h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={0.08 * i}>
              <div
                data-testid={`feature-card-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group relative rounded-2xl p-7 bg-white border border-slate-200 hover:border-emerald-400/70 transition-colors duration-300 h-full shadow-[0_0_25px_rgba(16,185,129,0.06)] hover:shadow-[0_0_35px_rgba(16,185,129,0.2)]"
              >
                <span className="w-11 h-11 rounded-xl bg-emerald-500/12 border border-emerald-500/35 flex items-center justify-center mb-5 group-hover:bg-emerald-500/20 transition-colors duration-300">
                  <f.icon className="w-5 h-5 text-emerald-600" />
                </span>
                <h3 className="text-lg font-semibold text-slate-900">{f.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mt-2">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Manifesto chapters */}
      <section id="manifesto" className="border-t border-slate-200 bg-slate-900/30" data-testid="landing-manifesto-section">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-28">
          <Reveal>
            <p className="text-xs font-mono2 uppercase tracking-[0.2em] text-emerald-600 font-semibold mb-3">Manifesto</p>
          </Reveal>
          <div className="space-y-20 mt-10">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={0.05}>
                <div className="grid grid-cols-12 gap-6 items-start" data-testid={`manifesto-chapter-${c.n}`}>
                  <div className="col-span-3 sm:col-span-2 font-mono2 text-4xl sm:text-5xl font-bold outline-text">{c.n}</div>
                  <div className="col-span-9 sm:col-span-10">
                    <h3 className="text-xl sm:text-2xl font-semibold tracking-tight text-slate-900">{c.title}</h3>
                    <p className="text-sm sm:text-base text-slate-600 leading-relaxed mt-3 max-w-2xl">{c.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-20">
            <Link to="/login" data-testid="manifesto-cta-btn"
              className="inline-flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold px-7 py-3.5 transition-colors duration-200">
              Open the Portal <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 overflow-hidden" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div>
              <span className="bg-white rounded-lg px-3 py-1.5 inline-flex">
                <img src="/logo.png" alt="GREENRGY" className="h-7" />
              </span>
              <p className="text-sm text-slate-500 mt-4 max-w-xs leading-relaxed">
                GreeErgy Green Tech Private Limited — precision accounting for clean energy distribution across India.
              </p>
            </div>
            <div className="text-sm">
              <p className="text-xs font-mono2 uppercase tracking-[0.2em] text-slate-500 mb-4">Portal</p>
              <div className="space-y-2.5 text-slate-600">
                <a href="#slabs" className="block hover:text-emerald-600 transition-colors duration-200">Price Slabs</a>
                <a href="#features" className="block hover:text-emerald-600 transition-colors duration-200">Features</a>
                <Link to="/login" className="block hover:text-emerald-600 transition-colors duration-200">Portal Login</Link>
              </div>
            </div>
            <div className="text-sm">
              <p className="text-xs font-mono2 uppercase tracking-[0.2em] text-slate-500 mb-4">Compliance</p>
              <p className="text-slate-500 leading-relaxed">
                GST-ready invoicing with HSN mapping and CGST/SGST computation. GSTIN-verified parties only for B2B trade.
              </p>
            </div>
          </div>
          <div className="mt-16 select-none pointer-events-none" aria-hidden>
            <div className="font-display font-extrabold tracking-tight leading-none outline-text text-[18vw] text-center">GREENRGY</div>
          </div>
          <p className="text-center text-xs text-slate-600 mt-4 font-mono2">© 2026 GreeErgy Green Tech Pvt Ltd · Powered by clean energy & clean books</p>
        </div>
      </footer>
    </div>
  );
}
